/**
 * ChangeActivityPanel — the Google Ads write-pipeline surface.
 *
 * Two altitudes share this component:
 *
 *  - INBOX (no accountId): the global needs-attention queue — pending
 *    proposals, in-flight applies, and failures across ALL accounts. Lives on
 *    the portfolio so an Andrei draft for an account you never open still
 *    gets seen. Applied/undone history is deliberately NOT here.
 *
 *  - ACCOUNT (accountId set): the full change story for ONE account —
 *    proposals awaiting review, applied changes with undo, failures, plus
 *    reverted/rejected rows as muted history. Lives on the account page next
 *    to the controls that create the changes.
 *
 * The master kill switch renders in the head of both modes, so "writes
 * ARMED/disarmed" is visible wherever a write can be approved. All status
 * mutations go through the owner-gated cloud functions (client writes to
 * googleAdsChangeSets are forbidden by firestore.rules).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, doc, query as fsQuery, where } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { db } from '../../../firebase';
import { decideChangeSet, setWritesEnabled } from './googleAdsApi';

export interface ChangeActivityPanelProps {
  /** Scope to one account (account page). Omit for the global inbox. */
  accountId?: string;
  /** Portfolio only: navigate to an account when its name is clicked. */
  onSelectAccount?: (customerId: string) => void;
}

/** Inbox mode shows only the statuses that need eyes on them. */
const INBOX_STATUSES = ['pending_review', 'applying', 'failed'];
/** Account mode: cap the terminal-state history tail (all actionable rows always show). */
const HISTORY_CAP = 30;

/** Human one-line description of a change-set operation (any op type).
 *  Exported so the unified approvals inbox renders GAds proposals identically. */
export function describeOp(op: any): string {
  // Fall back to a short resource id when a proposal stored no entityLabel (e.g.
  // older agent drafts that passed only ids) so the row never renders blank "".
  const lbl = op?.entityLabel || (op?.resourceName ? `#${String(op.resourceName).split('/').pop()}` : '');
  const money = (m: any) => (Number(m || 0) / 1e6).toFixed(2);
  switch (op?.type) {
    case 'negativeKeyword.add': return `+ neg “${op.keyword?.text}” [${String(op.keyword?.matchType || '').toLowerCase()}] → ${lbl}`;
    case 'negativeKeyword.remove': return `− neg “${op.before?.text || ''}” ← ${lbl}`;
    case 'keyword.setStatus': return `${op.after?.status === 'PAUSED' ? 'pause' : 'enable'} keyword “${lbl}”`;
    case 'ad.setStatus': return `${op.after?.status === 'PAUSED' ? 'pause' : 'enable'} ad “${lbl}”`;
    case 'adGroup.setStatus': return `${op.after?.status === 'PAUSED' ? 'pause' : 'enable'} ad group “${lbl}”`;
    case 'campaign.setStatus': return `${op.after?.status === 'PAUSED' ? 'pause' : 'enable'} campaign “${lbl}”`;
    case 'campaign.setBudget': return `budget → ${money(op.after?.amountMicros)} (was ${money(op.before?.amountMicros)}) · ${lbl}`;
    case 'keyword.setBid': return `keyword bid → ${money(op.after?.cpcBidMicros)} · ${lbl}`;
    case 'adGroup.setBid': return `ad-group bid → ${money(op.after?.cpcBidMicros)} · ${lbl}`;
    case 'keyword.add': return `+ keyword “${op.keyword?.text}” [${String(op.keyword?.matchType || '').toLowerCase()}] → ${lbl}`;
    case 'recommendation.apply': return `apply recommendation${op.recSummary ? `: ${op.recSummary}` : (lbl ? `: ${lbl}` : '')}`;
    case 'recommendation.dismiss': return `dismiss recommendation${op.recSummary ? `: ${op.recSummary}` : (lbl ? `: ${lbl}` : '')}`;
    case 'campaign.setDeviceBidModifier': return `${String(op.device || '').toLowerCase()} bid ${op.after?.bidModifier === 0 ? '−100% (exclude)' : `×${Number(op.after?.bidModifier).toFixed(2)}`} · ${lbl}`;
    case 'sharedList.create': return `create negative list “${op.listName}”`;
    case 'sharedList.addNegative': return `+ list neg “${op.keyword?.text}” → ${lbl}`;
    case 'sharedList.attachCampaign': return `attach negative list → ${lbl}`;
    case 'adGroup.create': return `+ ad group “${op.name}” (paused) → ${lbl}`;
    case 'ad.createRSA': return `+ ad (paused) → ${lbl}`;
    case 'asset.addSitelink': return `+ sitelink “${op.asset?.text}” → ${lbl}`;
    case 'asset.addCallout': return `+ callout “${op.asset?.text}” → ${op.linkScope === 'account' ? 'account' : lbl}`;
    case 'campaign.setLocationBidModifier': return `location bid ×${Number(op.after?.bidModifier).toFixed(2)} · ${lbl}`;
    case 'campaign.addAdSchedule': return `+ schedule ${String(op.schedule?.dayOfWeek || '').slice(0, 3).toLowerCase()} ${op.schedule?.startHour}–${op.schedule?.endHour}${op.after?.bidModifier != null ? ` ×${Number(op.after.bidModifier).toFixed(2)}` : ''} · ${lbl}`;
    case 'campaign.setAdScheduleBidModifier': return `schedule bid ×${Number(op.after?.bidModifier).toFixed(2)} · ${lbl}`;
    default: return String(op?.type || 'change');
  }
}

export const ChangeActivityPanel: React.FC<ChangeActivityPanelProps> = ({ accountId, onSelectAccount }) => {
  const [changeSets, setChangeSets] = useState<any[]>([]);
  const [writesOn, setWritesOn] = useState(false);
  const [deciding, setDeciding] = useState<string | null>(null);
  const [arming, setArming] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulk, setBulk] = useState<{ running: boolean; done: number; total: number; action: 'apply' | 'reject' }>({ running: false, done: 0, total: 0, action: 'apply' });
  const [confirmModal, setConfirmModal] = useState<{ title: string; body: React.ReactNode; confirmLabel: string; danger?: boolean; onConfirm: () => void | Promise<void> } | null>(null);

  // ── Subscription: account-scoped (all statuses) or global inbox ─────
  useEffect(() => {
    setChangeSets([]);
    setSelectedIds(new Set());
    const q = accountId
      ? fsQuery(collection(db, 'googleAdsChangeSets'), where('accountId', '==', accountId))
      : fsQuery(collection(db, 'googleAdsChangeSets'), where('status', 'in', INBOX_STATUSES));
    const unsub = onSnapshot(
      q,
      (snap) => setChangeSets(snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }))),
      (err) => { console.warn('[ChangeActivityPanel] changeSets sub error:', err); },
    );
    return () => unsub();
  }, [accountId]);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'settings', 'google_ads_writes_enabled'),
      (snap) => setWritesOn(snap.exists() ? (snap.data() as any).enabled === true : false),
      () => { /* best-effort */ });
    return () => unsub();
  }, []);

  // ── Decide (apply / reject / revert) ────────────────────────────────
  const runDecide = useCallback(async (id: string, action: 'apply' | 'reject' | 'revert') => {
    setDeciding(id);
    try {
      const r = await decideChangeSet(id, action);
      if (!r.ok) throw new Error(r.error || `${action} failed`);
      toast.success(action === 'apply' ? 'Applied to Google Ads.' : action === 'revert' ? 'Change undone.' : 'Rejected.');
    } catch (e: any) {
      toast.error(`${action} failed: ${e?.message || 'unknown error'}`);
    } finally {
      setDeciding(null);
    }
  }, []);

  const decide = useCallback((id: string, action: 'apply' | 'reject' | 'revert', summary?: string, warn?: string) => {
    // Reject is non-destructive (nothing touches the account) → no confirm.
    if (action === 'reject') { void runDecide(id, 'reject'); return; }
    setConfirmModal({
      title: action === 'apply' ? 'Apply change to live account' : 'Undo applied change',
      body: action === 'apply'
        ? <>Apply <strong>{summary || 'this change'}</strong> to the <strong>LIVE</strong> Google Ads account now?</>
        : <>Undo <strong>{summary || 'this applied change'}</strong>? This reverses what it changed on the live account.{warn ? <><br /><span style={{ color: '#d8a857' }}>{warn}</span></> : null}</>,
      confirmLabel: action === 'apply' ? 'Apply' : 'Undo',
      danger: action === 'revert',
      onConfirm: () => runDecide(id, action),
    });
  }, [runDecide]);

  // ── Master kill switch ──────────────────────────────────────────────
  const runArm = useCallback(async (enabled: boolean) => {
    setArming(true);
    try {
      const r = await setWritesEnabled(enabled);
      if (!r.ok) throw new Error(r.error || 'failed');
      toast.success(enabled ? 'Writes ARMED.' : 'Writes disarmed.');
    } catch (e: any) {
      toast.error(`Couldn't ${enabled ? 'arm' : 'disarm'}: ${e?.message || 'error'}`);
    } finally {
      setArming(false);
    }
  }, []);

  const armWrites = useCallback((enabled: boolean) => {
    if (!enabled) { void runArm(false); return; }      // disarming is always safe → no confirm
    setConfirmModal({
      title: 'Arm Google Ads writes',
      body: <>Arm writes? Approved change sets will then be able to modify <strong>LIVE</strong> accounts until you disarm. This is the master safety switch.</>,
      confirmLabel: 'Arm writes',
      danger: true,
      onConfirm: () => runArm(true),
    });
  }, [runArm]);

  // ── Ordering: needs-attention first, then time-ordered history ──────
  const { visibleSets, olderCount } = useMemo(() => {
    const attentionOrder: Record<string, number> = { failed: 0, pending_review: 1, applying: 2 };
    const byTimeDesc = (a: any, b: any) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0);
    // An applied set whose Undo button renders must NEVER fall off the capped
    // tail — undo is an action, not history. Mirrors the render-time gate.
    const isUndoable = (c: any) => c.status === 'applied'
      && (c.compound === true || (Array.isArray(c.operations) && c.operations.some((o: any) => o?.reversible !== false)));
    const attention = changeSets
      .filter((c) => attentionOrder[c.status] !== undefined)
      .sort((a, b) => {
        const sa = attentionOrder[a.status]; const sb = attentionOrder[b.status];
        if (sa !== sb) return sa - sb;
        return byTimeDesc(a, b);
      });
    const undoable = changeSets.filter(isUndoable).sort(byTimeDesc);
    // Remaining terminal rows (reverted / rejected / non-undoable applied)
    // read naturally in pure time order, capped. Inbox mode never receives
    // these (its query excludes them).
    const history = changeSets
      .filter((c) => attentionOrder[c.status] === undefined && !isUndoable(c))
      .sort(byTimeDesc);
    const capped = history.slice(0, HISTORY_CAP);
    return { visibleSets: [...attention, ...undoable, ...capped], olderCount: history.length - capped.length };
  }, [changeSets]);

  const pendingSets = useMemo(() => visibleSets.filter((c) => c.status === 'pending_review'), [visibleSets]);
  const pendingChangeCount = pendingSets.length;
  const isValid = (c: any) => !(c.validation && c.validation.ok === false);
  const selectedPending = useMemo(() => pendingSets.filter((c) => selectedIds.has(c.id)), [pendingSets, selectedIds]);
  const allPendingSelected = pendingSets.length > 0 && pendingSets.every((c) => selectedIds.has(c.id));

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => { const s = new Set(prev); if (s.has(id)) s.delete(id); else s.add(id); return s; });
  }, []);
  const toggleSelectAll = useCallback(() => {
    setSelectedIds((prev) => (pendingSets.length > 0 && pendingSets.every((c) => prev.has(c.id))) ? new Set() : new Set(pendingSets.map((c) => c.id)));
  }, [pendingSets]);

  // Apply/reject many change sets sequentially (safer than parallel: each
  // re-validates server-side + avoids quota bursts), with live progress.
  const runBulk = useCallback(async (ids: string[], action: 'apply' | 'reject') => {
    setBulk({ running: true, done: 0, total: ids.length, action });
    let ok = 0; let fail = 0;
    for (let i = 0; i < ids.length; i++) {
      setBulk({ running: true, done: i, total: ids.length, action });
      try {
        const r = await decideChangeSet(ids[i], action);
        if (!r.ok) throw new Error(r.error || 'failed');
        ok++;
      } catch { fail++; }
    }
    setBulk({ running: false, done: ids.length, total: ids.length, action });
    setSelectedIds(new Set());
    const verb = action === 'apply' ? 'Applied' : 'Rejected';
    if (fail === 0) toast.success(`${verb} ${ok} change${ok === 1 ? '' : 's'}.`);
    else toast.warn(`${verb} ${ok}, ${fail} failed — check the panel.`);
  }, []);

  const bulkApprove = useCallback(() => {
    const valid = selectedPending.filter(isValid);
    const skipped = selectedPending.length - valid.length;
    if (!valid.length) { toast.info('Select one or more pending changes that pass validation.'); return; }
    const ids = valid.map((c) => c.id);
    setConfirmModal({
      title: `Approve & apply ${ids.length} change${ids.length === 1 ? '' : 's'}`,
      body: <>Apply <strong>{ids.length}</strong> selected change{ids.length === 1 ? '' : 's'} to <strong>LIVE</strong> Google Ads account{accountId ? '' : 's'} now?{skipped > 0 ? <><br /><span style={{ color: '#d8a857' }}>{skipped} with validation errors will be skipped.</span></> : null}</>,
      confirmLabel: `Apply ${ids.length}`,
      danger: true,
      onConfirm: () => runBulk(ids, 'apply'),
    });
  }, [selectedPending, runBulk, accountId]);

  const bulkReject = useCallback(() => {
    const ids = selectedPending.map((c) => c.id);
    if (!ids.length) { toast.info('Select one or more pending changes first.'); return; }
    setConfirmModal({
      title: `Reject ${ids.length} change${ids.length === 1 ? '' : 's'}`,
      body: <>Reject <strong>{ids.length}</strong> selected pending change{ids.length === 1 ? '' : 's'}? They will not be applied.</>,
      confirmLabel: `Reject ${ids.length}`,
      onConfirm: () => runBulk(ids, 'reject'),
    });
  }, [selectedPending, runBulk]);

  // Close the modal immediately, then run — progress shows via the row spinner,
  // the arm spinner, or the in-panel bulk progress bar (not behind the overlay).
  const onConfirmClick = useCallback(() => {
    if (!confirmModal) return;
    const fn = confirmModal.onConfirm;
    setConfirmModal(null);
    void Promise.resolve().then(fn);
  }, [confirmModal]);

  return (
    <div className="ads-portfolio__writes">
      <div className="ads-portfolio__writes-head">
        <span>
          {accountId ? '✎ change activity' : '✎ proposals'}
          {pendingChangeCount ? ` · ${pendingChangeCount} awaiting review` : ''}
        </span>
        <span className={`ads-portfolio__killswitch ads-portfolio__killswitch--${writesOn ? 'on' : 'off'}`}>
          writes {writesOn ? 'ARMED' : 'disarmed'}
          <button type="button" className="ads-portfolio__reload" disabled={arming} onClick={() => armWrites(!writesOn)}>
            {arming ? '…' : writesOn ? '[disarm]' : '[arm]'}
          </button>
        </span>
      </div>

      {pendingSets.length > 0 && (
        <div className="ads-portfolio__bulk">
          <label className="ads-portfolio__bulk-all">
            <input type="checkbox" checked={allPendingSelected} onChange={toggleSelectAll} disabled={bulk.running} />
            <span>{selectedPending.length ? `${selectedPending.length} selected` : `select all pending (${pendingSets.length})`}</span>
          </label>
          <span className="ads-portfolio__bulk-actions">
            {bulk.running ? (
              <span className="ads-portfolio__bulk-progress">{bulk.action === 'reject' ? 'rejecting' : 'applying'} {bulk.done}/{bulk.total}…</span>
            ) : (<>
              <button type="button" className="ads-portfolio__change-approve" disabled={!selectedPending.length || !writesOn} title={!writesOn ? 'Arm writes first' : 'Approve & apply all selected to the live accounts'} onClick={bulkApprove}>Approve selected{selectedPending.length ? ` (${selectedPending.length})` : ''}</button>
              <button type="button" className="ads-portfolio__change-reject" disabled={!selectedPending.length} onClick={bulkReject}>Reject selected</button>
            </>)}
          </span>
        </div>
      )}

      {visibleSets.length === 0 && (
        <div className="ads-portfolio__writes-note">
          {accountId ? 'no change activity for this account yet' : 'no proposals awaiting review'}
        </div>
      )}

      {visibleSets.map((c) => {
        const terminal = c.status === 'reverted' || c.status === 'rejected';
        return (
          <div key={c.id} className={`ads-portfolio__change ads-portfolio__change--${c.status}`}>
            <div className="ads-portfolio__change-main">
              {c.status === 'pending_review' && (
                <input type="checkbox" className="ads-portfolio__change-check" checked={selectedIds.has(c.id)} onChange={() => toggleSelect(c.id)} disabled={bulk.running} title="Select for bulk approve / reject" />
              )}
              {/* Account name is only a navigation affordance on the global
                  inbox; on the account page every row IS this account. */}
              {!accountId && onSelectAccount && (
                <button type="button" className="ads-portfolio__alert-acct" onClick={() => c.accountId && onSelectAccount(c.accountId)} title="Open account">{c.accountName || c.accountId}</button>
              )}
              <span className="ads-portfolio__change-summary">{c.summary}</span>
              <span className="ads-portfolio__change-status">{String(c.status || '').replace('_', ' ')}{c.source ? ` · ${c.source}` : ''}</span>
            </div>
            {c.compound === true && c.plan && (c.kind === 'adGroup.createFull' ? (
              // Compound ad-group build — render the proposed ad group (keywords +
              // one ad) into an EXISTING campaign. Its plan has none of the
              // campaign-create fields (budget/geo/languages/adGroups[]), so it
              // gets its own branch instead of the phantom "0/day · 🌍 NO GEO".
              <div className="ads-portfolio__change-plan">
                <div className="ads-portfolio__change-plan-ag">
                  <div className="ads-portfolio__change-plan-agname">▸ {c.plan.name}{c.plan.cpcBidMicros ? ` · max CPC ${(Number(c.plan.cpcBidMicros) / 1e6).toFixed(2)}` : ''}{c.plan.campaignId ? ` · → campaign #${c.plan.campaignId}` : ''}</div>
                  <div className="ads-portfolio__change-plan-kws">{(c.plan.keywords || []).map((k: any) => `${k.text} [${String(k.matchType || '').toLowerCase()}]`).join('  ·  ')}</div>
                  {c.plan.ad && (
                    <div className="ads-portfolio__change-ad">
                      <div className="ads-portfolio__change-ad-meta">{c.plan.ad.finalUrl}</div>
                      <div className="ads-portfolio__change-ad-h">{(c.plan.ad.headlines || []).map((h: any) => typeof h === 'string' ? h : (h?.pinnedField ? `📌${h.text}` : h?.text)).join('  |  ')}</div>
                      <div className="ads-portfolio__change-ad-d">{(c.plan.ad.descriptions || []).map((d: any) => typeof d === 'string' ? d : d?.text).join('  ·  ')}</div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              // Compound campaign create — render the full proposed build for review.
              <div className="ads-portfolio__change-plan">
                <div className="ads-portfolio__change-plan-line">
                  <strong>{(Number(c.plan.budgetMicros || 0) / 1e6).toFixed(0)}/day</strong>
                  {' · '}{String(c.plan.bidding?.strategy || '').replace(/_/g, ' ').toLowerCase()}
                  {c.plan.bidding?.targetCpaMicros ? ` (tCPA ${(Number(c.plan.bidding.targetCpaMicros) / 1e6).toFixed(0)})` : ''}
                  {c.plan.bidding?.targetRoas ? ` (tROAS ${c.plan.bidding.targetRoas}×)` : ''}
                  {' · '}{c.plan.networks?.searchPartners ? 'search + partners' : 'google search only'}
                  {Array.isArray(c.plan.locations) && c.plan.locations.length > 0
                    ? <> · 📍 {c.plan.locations.map((l: any) => l.name || l.id).join(', ')}</>
                    : <> · <span style={{ color: '#d66', fontWeight: 700 }}>🌍 NO GEO TARGETING — worldwide</span></>}
                  {Array.isArray(c.plan.languages) && c.plan.languages.length > 0
                    ? <> · 🗣 {c.plan.languages.map((l: any) => l.name || l.id).join(', ')}</>
                    : <> · <span style={{ color: '#d8a857' }}>all languages</span></>}
                  {Array.isArray(c.plan.adSchedule) && c.plan.adSchedule.length > 0 && <> · 🕒 {c.plan.adSchedule.map((s: any) => `${String(s.dayOfWeek).slice(0, 3).toLowerCase()} ${s.startHour}–${s.endHour}`).join(', ')}</>}
                </div>
                {(c.plan.adGroups || []).map((g: any, gi: number) => (
                  <div key={gi} className="ads-portfolio__change-plan-ag">
                    <div className="ads-portfolio__change-plan-agname">▸ {g.name}{g.cpcBidMicros ? ` · max CPC ${(Number(g.cpcBidMicros) / 1e6).toFixed(2)}` : ''}</div>
                    <div className="ads-portfolio__change-plan-kws">{(g.keywords || []).map((k: any) => `${k.text} [${String(k.matchType || '').toLowerCase()}]`).join('  ·  ')}</div>
                    {(g.ads || []).map((a: any, ai: number) => (
                      <div key={ai} className="ads-portfolio__change-ad">
                        <div className="ads-portfolio__change-ad-meta">{a.finalUrl}</div>
                        <div className="ads-portfolio__change-ad-h">{(a.headlines || []).map((h: any) => h.pinnedField ? `📌${h.text}` : h.text).join('  |  ')}</div>
                        <div className="ads-portfolio__change-ad-d">{(a.descriptions || []).map((d: any) => d.text).join('  ·  ')}</div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            ))}
            {Array.isArray(c.operations) && c.operations.length > 0 && (
              <div className="ads-portfolio__change-ops">
                {c.operations.slice(0, 8).map((op: any, i: number) => (
                  op?.type === 'ad.createRSA' && op?.rsa
                    // Ad copy needs real review — render the full creative, not a one-liner.
                    ? (
                      <div key={i} className="ads-portfolio__change-ad">
                        <div className="ads-portfolio__change-ad-meta">{describeOp(op)} · {op.rsa.finalUrl}</div>
                        <div className="ads-portfolio__change-ad-h">{(op.rsa.headlines || []).map((h: any) => h.pinnedField ? `📌${h.text}` : h.text).join('  |  ')}</div>
                        <div className="ads-portfolio__change-ad-d">{(op.rsa.descriptions || []).map((d: any) => d.text).join('  ·  ')}</div>
                      </div>
                    )
                    : <span key={i} className="ads-portfolio__change-op">{describeOp(op)}</span>
                ))}
                {c.operations.length > 8 && <span className="ads-portfolio__change-op">+{c.operations.length - 8} more…</span>}
              </div>
            )}
            {c.rationale && <div className="ads-portfolio__change-rationale">{c.rationale}</div>}
            {c.validation && !c.validation.ok && !terminal && <div className="ads-portfolio__change-warn">⚠ validation: {(c.validation.errors || []).join('; ')}</div>}
            {c.status === 'failed' && c.error && <div className="ads-portfolio__change-warn">✗ {c.error}</div>}
            {!terminal && (
              <div className="ads-portfolio__change-actions">
                {c.status === 'pending_review' && (<>
                  <button type="button" className="ads-portfolio__change-approve" disabled={deciding === c.id || !writesOn || (c.validation && !c.validation.ok)} title={!writesOn ? 'Arm writes first' : (c.validation && !c.validation.ok) ? 'Fix validation first' : 'Apply to the live account'} onClick={() => decide(c.id, 'apply', c.summary)}>{deciding === c.id ? '…' : 'Approve & apply'}</button>
                  <button type="button" className="ads-portfolio__change-reject" disabled={deciding === c.id} onClick={() => decide(c.id, 'reject')}>Reject</button>
                </>)}
                {c.status === 'failed' && (<>
                  {Array.isArray(c.operations) && c.operations.some((o: any) => o?.appliedResourceName) && (
                    <button type="button" className="ads-portfolio__change-undo" disabled={deciding === c.id || !writesOn} title={!writesOn ? 'Arm writes to undo' : 'This set PARTIALLY applied before failing — undo the part that did apply'} onClick={() => decide(c.id, 'revert', c.summary, 'This set partially applied before failing — only the applied part is undone.')}>{deciding === c.id ? '…' : 'Undo applied part'}</button>
                  )}
                  <button type="button" className="ads-portfolio__change-reject" disabled={deciding === c.id} onClick={() => decide(c.id, 'reject')}>Dismiss</button>
                </>)}
                {c.status === 'applied' && (c.compound === true || (Array.isArray(c.operations) && c.operations.some((o: any) => o?.reversible !== false))
                  ? (<button type="button" className="ads-portfolio__change-undo" disabled={deciding === c.id || !writesOn} title={!writesOn ? 'Arm writes to undo' : c.compound ? 'Remove the entire created campaign' : 'Remove what this added'} onClick={() => decide(c.id, 'revert', c.summary, c.compound === true ? 'This PERMANENTLY removes the whole created campaign — budget, ad groups, keywords and ads. It cannot be un-removed (re-create if needed).' : Array.isArray(c.operations) && c.operations.some((o: any) => o?.type === 'adGroup.create') ? 'Removing a created ad group also permanently removes any keywords/ads since added to it.' : undefined)}>{deciding === c.id ? '…' : 'Undo'}</button>)
                  : (<span className="ads-portfolio__change-note" title="This change cannot be undone from here — manage it in Google Ads.">not undoable here</span>))}
              </div>
            )}
          </div>
        );
      })}

      {olderCount > 0 && (
        <div className="ads-portfolio__writes-more">+{olderCount} older — full audit trail in Firestore (googleAdsWriteAudit)</div>
      )}

      {confirmModal && (
        <div className="gads-neg-modal__overlay" onClick={() => setConfirmModal(null)}>
          <div className="gads-neg-modal" onClick={(e) => e.stopPropagation()}>
            <div className="gads-neg-modal__title">{confirmModal.title}</div>
            <div className="gads-neg-modal__body">{confirmModal.body}</div>
            <div className="gads-neg-modal__actions">
              <button type="button" className="gads-neg-modal__cancel" onClick={() => setConfirmModal(null)}>Cancel</button>
              <button type="button" className={confirmModal.danger ? 'gads-neg-modal__danger' : 'gads-neg-modal__apply'} onClick={onConfirmClick}>{confirmModal.confirmLabel}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChangeActivityPanel;
